import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { BACKEND_URL } from "../lib/api";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  if (!audioRef.current && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
  }
  const [current, setCurrent] = useState(null); // {episode, storyTitle, cover}
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const saveTimer = useRef(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => {
      setPosition(audio.currentTime);
      if (current && Date.now() - saveTimer.current > 5000) {
        saveTimer.current = Date.now();
        api.post(`/episodes/${current.episode.id}/progress`, { position: Math.floor(audio.currentTime) }).catch(() => {});
      }
    };
    const onMeta = () => setDuration(audio.duration || 0);
    const onEnd = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [current]);

  const play = useCallback(async (episode, meta = {}) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (current && current.episode.id === episode.id) {
      audio.paused ? audio.play() : audio.pause();
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/episodes/${episode.id}/stream-url`);
      audio.src = `${BACKEND_URL}${data.path}`;
      setCurrent({ episode, storyTitle: meta.storyTitle || "", cover: meta.cover || "" });
      let resume = 0;
      try {
        const p = await api.get(`/episodes/${episode.id}/progress`);
        resume = p.data.position || 0;
      } catch (e) {}
      await audio.play();
      if (resume > 0 && resume < (audio.duration || 1e9)) audio.currentTime = resume;
    } catch (e) {
      throw e;
    } finally {
      setLoading(false);
    }
  }, [current]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    audio.paused ? audio.play() : audio.pause();
  }, [current]);

  const seek = useCallback((t) => {
    const audio = audioRef.current;
    if (audio) audio.currentTime = t;
  }, []);

  return (
    <PlayerContext.Provider value={{ current, isPlaying, position, duration, loading, play, toggle, seek }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);

export function fmtTime(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}
